import { SelectChangeEvent } from '@mui/material';
import { useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@verantos-dev/core-annotation-tool';

import { useGetAnnotationAgentSetupQuery } from '../../api/createProjectApiSlice';
import { useIsAgentStepEnabled } from '../../hooks/useCreateProjectWizardSteps';
import { useSyncAgentConcepts } from '../../hooks/useSyncAgentConcepts';
import {
  setAnnotationAgentEnabled,
  setAnnotationAgentModelId,
} from '../../state/createProjectSlice';
import { selectAnnotationAgentEnabled, selectAnnotationAgentModelId } from '../../state/selector';
import { AnnotationAgentSetupResponse } from '../../types/response';

// TODO: cleanup with cp_annotation_createproject_agent ff

type AgentAvailability = 'loading' | 'available' | 'notSetUp' | 'notAvailable';

const getAgentAvailability = (
  setup: AnnotationAgentSetupResponse | undefined,
  isError: boolean
): AgentAvailability => {
  // A failed request, including a 404 from an API without the setup route,
  // makes the agent unavailable rather than leaving the switch loading.
  if (isError) return 'notAvailable';
  if (!setup) return 'loading';
  switch (setup.unavailableReason) {
    case 'NO_AGENT_ACCOUNT':
    case 'MULTIPLE_AGENT_ACCOUNTS':
      return 'notSetUp';
    case 'MODEL_UNAVAILABLE':
      return 'notAvailable';
    default:
      return 'available';
  }
};

const UNAVAILABLE_MESSAGE_IDS: Partial<Record<AgentAvailability, string>> = {
  notSetUp: 'createProjectWizard.step.annotationAgent.unavailable.notSetUp',
  notAvailable: 'createProjectWizard.step.annotationAgent.unavailable.notAvailable',
};

interface UseAnnotationAgentStepProps {
  setRequiredFieldsFilled: (fieldsFilled: boolean) => void;
}

interface UseAnnotationAgentStepResult {
  isAgentEnabled: boolean;
  isSwitchDisabled: boolean;
  unavailableMessageId: string | null;
  handleAgentToggle: (checked: boolean) => void;
  modelId: string | null;
  modelOptions: { value: string; displayValue: string }[];
  handleModelChange: (event: SelectChangeEvent<unknown>) => void;
}

export const useAnnotationAgentStep = ({
  setRequiredFieldsFilled,
}: UseAnnotationAgentStepProps): UseAnnotationAgentStepResult => {
  const dispatch = useAppDispatch();
  const isAgentStepEnabled = useIsAgentStepEnabled();
  const { data: setup, isError } = useGetAnnotationAgentSetupQuery(undefined, {
    skip: !isAgentStepEnabled,
  });
  const isAgentEnabled = useAppSelector(selectAnnotationAgentEnabled);
  const modelId = useAppSelector(selectAnnotationAgentModelId);

  // The step only exists with the flag on, so it always merges when it mounts.
  useSyncAgentConcepts(true);

  const availability = getAgentAvailability(setup?.data, isError);
  const canTurnOn = availability === 'available';

  const modelOptions = useMemo(
    () =>
      (setup?.data.models ?? []).map((model) => ({
        value: model.modelId,
        displayValue: model.displayName,
      })),
    [setup]
  );

  // goToNextStep resets requiredFieldsFilled to false, so this step sets it
  // itself in both states, including when the switch is off.
  useEffect(() => {
    setRequiredFieldsFilled(!isAgentEnabled || modelId !== null);
  }, [isAgentEnabled, modelId, setRequiredFieldsFilled]);

  const handleAgentToggle = (checked: boolean) => {
    dispatch(setAnnotationAgentEnabled(checked));
  };

  const handleModelChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as string;
    if (modelOptions.some((option) => option.value === value)) {
      dispatch(setAnnotationAgentModelId(value));
    }
  };

  return {
    isAgentEnabled,
    // Once on, the switch stays usable so the agent can always be turned off.
    isSwitchDisabled: !isAgentEnabled && !canTurnOn,
    unavailableMessageId: UNAVAILABLE_MESSAGE_IDS[availability] ?? null,
    handleAgentToggle,
    modelId,
    modelOptions,
    handleModelChange,
  };
};
